import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import EmptyState from "@/components/base/EmptyState";
import Lightbox from "@/components/feature/Lightbox";
import { worksList } from "@/mocks/works";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";

export default function RecommendDetail() {
  const { id } = useParams();
  const item = useMemo(() => worksList.find((w) => w.id === id), [id]);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [zoom, setZoom] = useState<string | null>(null);

  usePageMeta(
    item ? `${item.title} - 动漫作品推荐 - 91动漫` : "作品详情 - 91动漫",
    item ? item.synopsis.slice(0, 80) : "91动漫作品详情页。",
    item ? `91动漫,${item.title},${item.category},动漫推荐` : "91动漫,动漫推荐"
  );

  useEffect(() => {
    setToast(null);
    setShareOpen(false);
    setZoom(null);
  }, [id]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2200);
  };

  const handleFavorite = () => {
    if (!item) return;
    const active = toggleFavorite(item.id);
    showToast(active ? "已加入我的收藏" : "已取消收藏");
  };

  const copyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast("链接已复制，快分享给同好吧");
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
    setShareOpen(false);
  };

  if (!item) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-16 md:px-8">
        <EmptyState
          title="没有找到这部作品"
          description="它可能已被移除，或链接有误。你可以返回作品推荐列表看看其他内容。"
          actionLabel="返回作品推荐"
          onAction={() => window.REACT_APP_NAVIGATE("/recommend")}
          icon="ri-file-unknow-line"
        />
      </div>
    );
  }

  const favorited = isFavorite(item.id);
  const related = worksList.filter((w) => w.id !== item.id && w.category === item.category).slice(0, 4);
  const relatedFallback = related.length
    ? related
    : worksList.filter((w) => w.id !== item.id).slice(0, 4);

  const shareUrl = `/recommend/${item.id}`;
  const shareText = `推荐一部动漫《${item.title}》`;

  return (
    <div className="w-full">
      <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-8 md:py-8">
        <Breadcrumb items={[{ label: "动漫作品推荐", to: "/recommend" }, { label: item.title }]} />
      </div>

      <section className="mx-auto max-w-[1280px] px-4 md:px-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr] lg:gap-10">
          <div>
            <button
              type="button"
              onClick={() => setZoom(item.cover)}
              className="group block w-full cursor-zoom-in overflow-hidden rounded-lg"
            >
              <img
                src={item.cover}
                alt={`${item.title} - 91动漫作品海报`}
                title={`${item.title} - 91动漫`}
                className="h-[420px] w-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </button>
          </div>

          <div className="flex flex-col">
            <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">
              {item.title}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-800">
                {item.category}
              </span>
              <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
                {item.region}
              </span>
              <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
                {item.year} 年
              </span>
              {item.tags.map((t) => (
                <span key={t} className="text-xs text-foreground-400">
                  #{t}
                </span>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 rounded-lg bg-background-100 px-4 py-3">
                <i className="ri-star-fill text-2xl text-primary-500" />
                <div className="leading-tight">
                  <div className="font-heading text-xl font-extrabold text-foreground-950">
                    {item.rating.toFixed(1)}
                  </div>
                  <div className="text-xs text-foreground-500">推荐指数</div>
                </div>
              </div>
              <div className="flex items-center gap-2 rounded-lg bg-background-100 px-4 py-3">
                <i className="ri-play-list-2-line text-2xl text-accent-600" />
                <div className="leading-tight">
                  <div className="font-heading text-xl font-extrabold text-foreground-950">
                    {item.episodes > 0 ? item.episodes : "待定"}
                  </div>
                  <div className="text-xs text-foreground-500">集数</div>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <h2 className="font-heading text-base font-bold text-foreground-900">作品简介</h2>
              <p className="mt-2 text-[15px] leading-[1.9] text-foreground-700">{item.synopsis}</p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleFavorite}
                className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-semibold transition-colors ${
                  favorited
                    ? "bg-primary-100 text-primary-800 hover:bg-primary-200"
                    : "bg-primary-500 text-white hover:bg-primary-600"
                }`}
              >
                <i className={favorited ? "ri-bookmark-fill text-base" : "ri-bookmark-line text-base"} />
                {favorited ? "已收藏" : "收藏作品"}
              </button>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShareOpen((v) => !v)}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
                >
                  <i className="ri-share-forward-line text-base" />
                  分享
                </button>
                {shareOpen && (
                  <div className="absolute left-0 top-full z-20 mt-2 w-52 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-1.5 shadow-sm">
                    <button
                      type="button"
                      onClick={copyLink}
                      className="flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                    >
                      <i className="ri-link" />
                      复制链接
                    </button>
                    <a
                      href={`https://service.weibo.com/share/share.php?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                    >
                      <i className="ri-weibo-fill" />
                      分享到微博
                    </a>
                    <a
                      href="#"
                      onClick={(e) => e.preventDefault()}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                    >
                      <i className="ri-bilibili-fill" />
                      分享到哔哩哔哩
                    </a>
                  </div>
                )}
              </div>

              <Link
                to="/recommend"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
              >
                <i className="ri-arrow-left-line" />
                返回列表
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-book-open-line text-xl text-primary-600" />
              章节与观看指南
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.guide}</p>
            <div className="mt-4 rounded-md bg-secondary-50 p-4">
              <p className="flex items-start gap-2 text-xs leading-relaxed text-secondary-700">
                <i className="ri-information-line mt-0.5 text-base" />
                本站仅提供作品资料与观看建议，不提供在线播放与下载服务，具体观看渠道请以官方发布为准。
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-list-check-2 text-xl text-accent-600" />
              作品信息
            </h2>
            <ul className="mt-3 divide-y divide-background-100 text-sm">
              <li className="flex items-center justify-between py-2.5">
                <span className="text-foreground-500">类型</span>
                <span className="font-medium text-foreground-900">{item.category}</span>
              </li>
              <li className="flex items-center justify-between py-2.5">
                <span className="text-foreground-500">地区</span>
                <span className="font-medium text-foreground-900">{item.region}</span>
              </li>
              <li className="flex items-center justify-between py-2.5">
                <span className="text-foreground-500">年份</span>
                <span className="font-medium text-foreground-900">{item.year}</span>
              </li>
              <li className="flex items-center justify-between py-2.5">
                <span className="text-foreground-500">集数</span>
                <span className="font-medium text-foreground-900">
                  {item.episodes > 0 ? `${item.episodes} 集` : "待公布"}
                </span>
              </li>
              <li className="flex items-center justify-between py-2.5">
                <span className="text-foreground-500">标签</span>
                <span className="font-medium text-foreground-900">{item.tags.join(" / ")}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="mt-12 border-t border-background-200 bg-background-100/60">
        <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-8 md:py-12">
          <div className="flex items-center gap-2">
            <i className="ri-movie-2-line text-xl text-primary-600" />
            <h4 className="font-heading text-xl font-bold text-foreground-950">
              <Link to="/recommend" className="transition-colors hover:text-primary-700">
                相关作品推荐
              </Link>
            </h4>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5">
            {relatedFallback.map((w) => (
              <WorkCard key={w.id} item={w} />
            ))}
          </div>
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[90] -translate-x-1/2 rounded-full bg-secondary-950/90 px-5 py-2.5 text-sm text-white animate-fade-in">
          {toast}
        </div>
      )}

      {zoom && <Lightbox src={zoom} alt={item.title} onClose={() => setZoom(null)} />}
    </div>
  );
}